import PropTypes from "prop-types";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
} from "@mui/material";

import { CustomerSelect } from "@/features/customers";
import { PromotionTypeEnum } from "@/features/promotions";
import { apiClient, getErrorMessage } from "@/features/shared";
import { CustomDialogTitle, CustomInputField } from "@/features/ui";
import { useSnackbar } from "@/features/ui";

const PUBLIC_VALIDATION_SCHEMA = yup.object({
  code: yup.string().required("Kode promosi wajib diisi"),
});

const TARGETED_VALIDATION_SCHEMA = yup.object({
  customer: yup.object().nullable().required("Pelanggan wajib diisi"),
});

const PUBLIC_DEFAULT_VALUES = {
  code: "",
};

const TARGETED_DEFAULT_VALUES = {
  customer: null,
};

export const AddPromotionCodeFormDialog = ({
  isOpen,
  onClose,
  onSubmitted,
  promotionId,
  promotionType,
}) => {
  const { showSnackbar } = useSnackbar();
  const isPublic = promotionType === PromotionTypeEnum.PUBLIC;

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { isSubmitting, errors },
  } = useForm({
    defaultValues: isPublic ? PUBLIC_DEFAULT_VALUES : TARGETED_DEFAULT_VALUES,
    resolver: yupResolver(
      isPublic ? PUBLIC_VALIDATION_SCHEMA : TARGETED_VALIDATION_SCHEMA
    ),
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const onFormSubmit = async (data) => {
    try {
      const payload = isPublic
        ? { code: data.code }
        : { customerId: data.customer.id };

      await apiClient.post(
        `promotions/${promotionId}/promotion-codes`,
        payload
      );

      onSubmitted();
      handleClose();
    } catch (error) {
      showSnackbar(
        getErrorMessage(error, "Gagal menyimpan data")
      );
      throw error;
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth>
      <CustomDialogTitle
        variant="h5"
        title="Tambah Kode Promosi"
        onClose={handleClose}
      />
      <Box component="form" onSubmit={handleSubmit(onFormSubmit)} noValidate>
        <DialogContent>
          <Stack spacing={2}>
            {isPublic ? (
              <CustomInputField
                required
                name="code"
                label="Kode Promosi"
                register={register}
                helperText={errors.code?.message}
                error={Boolean(errors.code)}
              />
            ) : (
              <Controller
                control={control}
                name="customer"
                render={({ field, fieldState }) => (
                  <CustomerSelect
                    value={field.value}
                    onChange={field.onChange}
                    TextFieldProps={{
                      label: "Pelanggan",
                      required: true,
                      error: fieldState.invalid,
                      helperText: fieldState.error?.message,
                    }}
                  />
                )}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button loading={isSubmitting} variant="contained" type="submit">
            Tambah
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

AddPromotionCodeFormDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmitted: PropTypes.func.isRequired,
  promotionId: PropTypes.string.isRequired,
  promotionType: PropTypes.string.isRequired,
};
